import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DemoExportGate } from "@/components/DemoExportGate";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { AlertTriangle, Calculator, Download, Pencil, Plus, RefreshCw, Trash2, FileSpreadsheet, FileText, ChevronDown } from "lucide-react";
import {
  useGetEquitySummary,
  useGetEquityComparablePairs,
  useGetEquityTrend,
  useGetEquityProportionality,
  useListParticipationCounts,
  useUpsertParticipationCount,
  useDeleteParticipationCount,
  useListCoaches,
  useUpdateCoach,
  getListParticipationCountsQueryKey,
  getGetEquityProportionalityQueryKey,
  getListCoachesQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

const currentYear = new Date().getFullYear();

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}
function fmtPct(n: number | null | undefined, digits = 1): string {
  if (n == null) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

// Inline glossary affordance: makes the "pp" unit self-explanatory on hover
// AND for keyboard / screen-reader users via the abbr+title pattern.
function AbbrPP() {
  return (
    <abbr
      title="percentage points — the difference between two percentages"
      tabIndex={0}
      aria-label="percentage points"
      className="cursor-help underline decoration-dotted"
    >
      pp
    </abbr>
  );
}

function statusForGap(pp: number | null): { label: string; tone: "ok" | "watch" | "action" } {
  if (pp == null) return { label: "—", tone: "ok" };
  if (pp >= -5) return { label: "OK", tone: "ok" };
  if (pp >= -10) return { label: "Watch", tone: "watch" };
  return { label: "Action", tone: "action" };
}

export function EquityPanel({
  isAdmin,
  sport,
  initialFiscalYear,
  status,
  role,
}: {
  isAdmin: boolean;
  sport?: string;
  initialFiscalYear?: number;
  status?: "active" | "expired" | "all";
  role?: string;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [fy, setFy] = useState<number>(initialFiscalYear ?? currentYear);
  // Keep the local fiscal year in sync with the parent analytics filter bar.
  useEffect(() => {
    if (initialFiscalYear != null && initialFiscalYear !== fy) {
      setFy(initialFiscalYear);
    }
    // Intentionally only react to parent changes, not local user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiscalYear]);
  const [trendStart, setTrendStart] = useState<number>(currentYear - 4);
  const [trendEnd, setTrendEnd] = useState<number>(currentYear);

  const equityParams: { fiscalYear: number; sport?: string; status?: "active" | "expired" | "all"; role?: string } = { fiscalYear: fy };
  if (sport) equityParams.sport = sport;
  if (status) equityParams.status = status;
  if (role) equityParams.role = role;
  const [thresholdPct, setThresholdPct] = useState<number>(20);
  const pairsParams = { ...equityParams, thresholdPct: thresholdPct / 100 };
  const { data: summary, isLoading: summaryLoading } = useGetEquitySummary(equityParams);
  const { data: pairs } = useGetEquityComparablePairs(pairsParams);
  const trendParams: { fiscalYearStart: number; fiscalYearEnd: number; sport?: string; status?: "active" | "expired" | "all"; role?: string } = {
    fiscalYearStart: trendStart,
    fiscalYearEnd: trendEnd,
  };
  if (sport) trendParams.sport = sport;
  if (status) trendParams.status = status;
  if (role) trendParams.role = role;
  const { data: trend } = useGetEquityTrend(trendParams);
  const { data: proportionality } = useGetEquityProportionality(equityParams);

  const { data: participation } = useListParticipationCounts({ fiscalYear: fy });
  const { data: coachesList } = useListCoaches();
  const updateCoachMut = useUpdateCoach({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListCoachesQueryKey() });
      },
    },
  });

  const unassignedCoaches = useMemo(
    () => (coachesList ?? []).filter((c) => !c.programGender),
    [coachesList],
  );

  const sportsWithCoaches = useMemo(
    () => new Set((coachesList ?? []).map((c) => c.sport.replace(/^(Men's|Mens|Women's|Womens|Boys|Girls)\s+/i, "").trim().toLowerCase())),
    [coachesList],
  );
  const sportsWithParticipation = useMemo(
    () => new Set((participation ?? []).map((p) => p.sport.toLowerCase())),
    [participation],
  );
  const missingParticipation = useMemo(
    () => Array.from(sportsWithCoaches).filter((s) => s && !sportsWithParticipation.has(s)),
    [sportsWithCoaches, sportsWithParticipation],
  );

  const upsertMut = useUpsertParticipationCount({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListParticipationCountsQueryKey({ fiscalYear: fy }) });
        qc.invalidateQueries({ queryKey: getGetEquityProportionalityQueryKey({ fiscalYear: fy }) });
        toast({ title: "Saved", description: "Participation count saved." });
      },
      onError: () => toast({ title: "Save failed", variant: "destructive" }),
    },
  });
  const delMut = useDeleteParticipationCount({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListParticipationCountsQueryKey({ fiscalYear: fy }) });
        qc.invalidateQueries({ queryKey: getGetEquityProportionalityQueryKey({ fiscalYear: fy }) });
      },
    },
  });

  const yearOptions = useMemo(
    () => Array.from({ length: 11 }, (_, i) => currentYear - 5 + i),
    [],
  );

  const trendData = useMemo(
    () =>
      (trend ?? []).map((p) => ({
        year: String(p.fiscalYear),
        Mens: Math.round(p.mensTotal),
        Womens: Math.round(p.womensTotal),
        Ratio: p.ratio == null ? null : Number((p.ratio * 100).toFixed(1)),
      })),
    [trend],
  );

  async function downloadEquity(format: "csv" | "xlsx" = "csv") {
    try {
      const params = new URLSearchParams({ fiscalYear: String(fy) });
      if (sport) params.set("sport", sport);
      if (status) params.set("status", status);
      if (role) params.set("role", role);
      if (format === "xlsx") params.set("format", "xlsx");
      const res = await fetch(`/api/export/equity.csv?${params.toString()}`);
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `equity-${fy}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Export failed", variant: "destructive" });
    }
  }

  // ---- Equity Calculator (what-if) ----
  // Live values, derived from the current summary + participation totals.
  // Both data sources must respect the active filter set (currently fy + sport)
  // so the calculator agrees with the proportionality card above it.
  const liveMensSpend = Math.round(summary?.mens.totalGuaranteedComp ?? 0);
  const liveWomensSpend = Math.round(summary?.womens.totalGuaranteedComp ?? 0);
  const { liveMensAthletes, liveWomensAthletes } = useMemo(() => {
    let m = 0, w = 0;
    const sportLc = sport?.trim().toLowerCase();
    for (const p of participation ?? []) {
      // Apply the same sport filter the summary uses; participationCounts
      // API doesn't accept `sport` as a query param, so filter client-side.
      if (sportLc && p.sport.toLowerCase() !== sportLc) continue;
      if (p.programGender === "mens") m += p.athleteCount;
      else if (p.programGender === "womens") w += p.athleteCount;
    }
    return { liveMensAthletes: m, liveWomensAthletes: w };
  }, [participation, sport]);
  const [scMensSpend, setScMensSpend] = useState<number>(liveMensSpend);
  const [scWomensSpend, setScWomensSpend] = useState<number>(liveWomensSpend);
  const [scMensAthletes, setScMensAthletes] = useState<number>(liveMensAthletes);
  const [scWomensAthletes, setScWomensAthletes] = useState<number>(liveWomensAthletes);
  // Seed scenario inputs once per (fy, sport) filter combo, after BOTH
  // the spend summary AND the participation list have arrived. Seeding
  // when only `summary` is loaded would lock the athlete inputs at 0
  // (the participation request is still in-flight) and `seededFyRef`
  // would block the re-seed when participation lands. Re-key on sport
  // too because the calculator's live numbers are filtered by sport.
  // Do NOT reseed on subsequent refetches — that would wipe what-if
  // edits in the middle of the user's exploration. The "Reset to
  // current" button is the explicit way to re-pull live values.
  const seededKeyRef = useRef<string | null>(null);
  // Re-key on every active filter so a status/role change also reseeds.
  const seedKey = `${fy}|${sport ?? ""}|${status ?? ""}|${role ?? ""}`;
  useEffect(() => {
    if (seededKeyRef.current === seedKey) return;
    if (summary == null || participation == null) return;
    setScMensSpend(liveMensSpend);
    setScWomensSpend(liveWomensSpend);
    setScMensAthletes(liveMensAthletes);
    setScWomensAthletes(liveWomensAthletes);
    seededKeyRef.current = seedKey;
  }, [seedKey, summary, participation, liveMensSpend, liveWomensSpend, liveMensAthletes, liveWomensAthletes]);

  function ratios(menSpend: number, womenSpend: number, menAth: number, womenAth: number) {
    const totalSpend = menSpend + womenSpend;
    const totalAth = menAth + womenAth;
    const wSpendShare = totalSpend > 0 ? womenSpend / totalSpend : null;
    const wAthShare = totalAth > 0 ? womenAth / totalAth : null;
    const gapPP = wSpendShare != null && wAthShare != null ? (wSpendShare - wAthShare) * 100 : null;
    const spendRatio = womenSpend > 0 ? menSpend / womenSpend : null;
    const partRatio = womenAth > 0 ? menAth / womenAth : null;
    return { wSpendShare, wAthShare, gapPP, spendRatio, partRatio };
  }
  const liveCalc = ratios(liveMensSpend, liveWomensSpend, liveMensAthletes, liveWomensAthletes);
  const scenarioCalc = ratios(scMensSpend, scWomensSpend, scMensAthletes, scWomensAthletes);
  const liveStatus = statusForGap(liveCalc.gapPP);
  const scenarioStatus = statusForGap(scenarioCalc.gapPP);
  const toneClass = (t: "ok" | "watch" | "action") =>
    t === "ok" ? "text-emerald-600 dark:text-emerald-400"
    : t === "watch" ? "text-amber-600 dark:text-amber-400"
    : "text-red-600 dark:text-red-400";
  const toneBadge = (t: "ok" | "watch" | "action") =>
    t === "ok" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
    : t === "watch" ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200"
    : "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200";
  function resetScenario() {
    setScMensSpend(liveMensSpend);
    setScWomensSpend(liveWomensSpend);
    setScMensAthletes(liveMensAthletes);
    setScWomensAthletes(liveWomensAthletes);
  }

  // Inline form state for adding participation rows
  const [newSport, setNewSport] = useState("");
  const [newGender, setNewGender] = useState<"mens" | "womens" | "coed">("mens");
  const [newCount, setNewCount] = useState<string>("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editCount, setEditCount] = useState<string>("");

  function handleAddRow() {
    const count = parseInt(newCount, 10);
    if (!newSport.trim() || Number.isNaN(count) || count < 0) {
      toast({ title: "Invalid input", description: "Sport and a non-negative count are required.", variant: "destructive" });
      return;
    }
    upsertMut.mutate({
      data: {
        sport: newSport.trim(),
        programGender: newGender,
        fiscalYear: fy,
        athleteCount: count,
      },
    });
    setNewSport("");
    setNewCount("");
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Label className="text-sm">Fiscal year</Label>
        <Select value={String(fy)} onValueChange={(v) => setFy(parseInt(v))}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            {yearOptions.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DemoExportGate label="Equity Export" size="sm" variant="outline" className="ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="ml-auto">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Equity Export
                <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => downloadEquity("csv")}>
                <FileText className="h-3.5 w-3.5 mr-2" /> CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadEquity("xlsx")}>
                <FileSpreadsheet className="h-3.5 w-3.5 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </DemoExportGate>
      </div>

      {/* Plain-English explainer (Task #391 gap #5) */}
      <Card className="border-dashed bg-muted/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">How to read this dashboard</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-2">
          <p>
            Each coach is tagged with a <strong>program gender</strong> (men's, women's, or co-ed).
            Spend totals — annual base + signing bonus + performance bonuses — are then split by
            program gender so you can compare investment side-by-side.
          </p>
          <p>
            <strong>Comparable pairs</strong> match the men's and women's variants of the same base
            sport (e.g. "Mens Basketball" / "Womens Basketball") and flag any sport where the gap
            exceeds your disparity threshold.
          </p>
          <p>
            <strong>Spend vs Participation</strong> is the Title IX core proportionality test: if
            women receive 45% of spend but make up 50% of athletes, the gap is −5{" "}
            <AbbrPP /> (percentage points) and warrants review. Add athlete counts below to enable
            this calculation.
          </p>
          <div className="pt-2 mt-2 border-t border-border/60">
            <div className="font-medium text-foreground/80 mb-1">Threshold legend</div>
            <ul className="space-y-1">
              <li>
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-2 align-middle" />
                <strong>OK</strong> — pair gap below your disparity threshold (currently {thresholdPct}%);
                Title IX gap within ±5 <AbbrPP />.
              </li>
              <li>
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-2 align-middle" />
                <strong>Watch</strong> — pair gap at/above the threshold OR Title IX gap between −5 <AbbrPP /> and −10 <AbbrPP />.
              </li>
              <li>
                <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />
                <strong>Action</strong> — Title IX gap below −10 <AbbrPP />, or per-sport spend delta &gt; $100K
                in the drill-down. Consider re-balancing investment or documenting program-specific
                justification.
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Men's Guaranteed Comp ({fy})</div>
          <div className="text-2xl font-bold mt-1">{summaryLoading ? "…" : fmtMoney(summary?.mens.totalGuaranteedComp)}</div>
          <div className="text-xs text-muted-foreground mt-1">{summary?.mens.coachCount ?? 0} coaches</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Women's Guaranteed Comp ({fy})</div>
          <div className="text-2xl font-bold mt-1">{summaryLoading ? "…" : fmtMoney(summary?.womens.totalGuaranteedComp)}</div>
          <div className="text-xs text-muted-foreground mt-1">{summary?.womens.coachCount ?? 0} coaches</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Spend Gap (M − W)</div>
          <div className="text-2xl font-bold mt-1">{fmtMoney(summary?.gapDollars)}</div>
          <div className="text-xs text-muted-foreground mt-1">{fmtPct(summary?.gapPct)} of larger side</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Avg Head Coach Salary</div>
          <div className="text-sm font-bold mt-1">M: {fmtMoney(summary?.mens.avgHeadCoachSalary)}</div>
          <div className="text-sm font-bold">W: {fmtMoney(summary?.womens.avgHeadCoachSalary)}</div>
        </CardContent></Card>
      </div>

      {/* Bonus + Buyout exposure split by gender */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Men's Bonus Exposure ({fy})</div>
          <div className="text-2xl font-bold mt-1">{summaryLoading ? "…" : fmtMoney(summary?.mens.totalBonusExposure)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Women's Bonus Exposure ({fy})</div>
          <div className="text-2xl font-bold mt-1">{summaryLoading ? "…" : fmtMoney(summary?.womens.totalBonusExposure)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Men's Buyout Exposure ({fy})</div>
          <div className="text-2xl font-bold mt-1">{summaryLoading ? "…" : fmtMoney(summary?.mens.totalBuyoutExposure)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Women's Buyout Exposure ({fy})</div>
          <div className="text-2xl font-bold mt-1">{summaryLoading ? "…" : fmtMoney(summary?.womens.totalBuyoutExposure)}</div>
        </CardContent></Card>
      </div>

      {/* Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between gap-3">
            <span>Men's vs Women's Spend Trend</span>
            <div className="flex items-center gap-2 text-xs">
              <Select value={String(trendStart)} onValueChange={(v) => setTrendStart(parseInt(v))}>
                <SelectTrigger className="w-24 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-muted-foreground">→</span>
              <Select value={String(trendEnd)} onValueChange={(v) => setTrendEnd(parseInt(v))}>
                <SelectTrigger className="w-24 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trendData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-muted-foreground text-sm">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trendData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis
                  width={72}
                  tickFormatter={(v: number) => fmtMoney(v)}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip formatter={(v: number) => fmtMoney(v)} />
                <Legend />
                <Line type="monotone" dataKey="Mens" stroke="#3b82f6" />
                <Line type="monotone" dataKey="Womens" stroke="#ec4899" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Comparable pairs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between gap-3">
            <span>Comparable Sport Pairs (M vs W)</span>
            <label className="flex items-center gap-2 text-xs font-normal">
              <span className="text-muted-foreground">Disparity threshold</span>
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                value={thresholdPct}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!Number.isNaN(v)) setThresholdPct(Math.max(0, Math.min(100, v)));
                }}
                className="w-16 h-8 rounded-md border bg-background px-2 text-right text-sm"
                aria-label="Disparity flag threshold percentage"
              />
              <span className="text-muted-foreground">%</span>
            </label>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(pairs ?? []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">
              No comparable pairs found. Tag coaches with a program gender and matching base sport (e.g. "Mens Basketball" / "Womens Basketball") to populate this view.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sport</TableHead>
                  <TableHead className="text-right">Men's Comp</TableHead>
                  <TableHead className="text-right">Women's Comp</TableHead>
                  <TableHead className="text-right">Δ $</TableHead>
                  <TableHead className="text-right">Δ %</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(pairs ?? []).map((p) => (
                  <TableRow key={p.sport}>
                    <TableCell className="font-medium capitalize">{p.sport}</TableCell>
                    <TableCell className="text-right">{fmtMoney(p.mens.totalGuaranteedComp)}</TableCell>
                    <TableCell className="text-right">{fmtMoney(p.womens.totalGuaranteedComp)}</TableCell>
                    <TableCell className="text-right">{fmtMoney(p.deltaDollars)}</TableCell>
                    <TableCell className="text-right">{fmtPct(p.deltaPct)}</TableCell>
                    <TableCell>
                      {p.flagged ? (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="h-3 w-3" /> Disparity
                        </Badge>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Proportionality */}
      <Card>
        <CardHeader><CardTitle className="text-base">Spend vs Participation (Title IX core test)</CardTitle></CardHeader>
        <CardContent>
          {!proportionality?.hasParticipationData ? (
            <div className="text-sm text-muted-foreground">
              Add athlete participation counts below to compute the spend vs participation gap.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-muted-foreground">Spend ratio</div>
                <div className="text-sm">M {fmtPct(proportionality.spendRatio.mens)} · W {fmtPct(proportionality.spendRatio.womens)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Participation ratio</div>
                <div className="text-sm">M {fmtPct(proportionality.participationRatio.mens)} · W {fmtPct(proportionality.participationRatio.womens)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Women's spend − participation</div>
                <div className="text-sm font-semibold" aria-label={proportionality.gapPercentagePoints == null ? undefined : `${proportionality.gapPercentagePoints.toFixed(1)} percentage points`}>
                  {proportionality.gapPercentagePoints == null ? "—" : (
                    <>
                      {proportionality.gapPercentagePoints.toFixed(1)} <AbbrPP />
                    </>
                  )}
                  {proportionality.gapPercentagePoints != null && proportionality.gapPercentagePoints < -5 ? (
                    <Badge variant="destructive" className="ml-2 gap-1">
                      <AlertTriangle className="h-3 w-3" /> Under-funded
                    </Badge>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Equity Calculator (what-if) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4 text-muted-foreground" />
            Equity Calculator (what-if)
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Model what would happen to the spend / participation gap if you adjusted spend or athlete
            counts. Inputs are pre-filled from the current FY {fy} numbers; nothing is saved.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label htmlFor="sc-mens-spend" className="text-xs">Men's spend ($)</Label>
              <Input id="sc-mens-spend" type="number" min="0" value={scMensSpend}
                onChange={(e) => setScMensSpend(Math.max(0, parseFloat(e.target.value) || 0))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="sc-womens-spend" className="text-xs">Women's spend ($)</Label>
              <Input id="sc-womens-spend" type="number" min="0" value={scWomensSpend}
                onChange={(e) => setScWomensSpend(Math.max(0, parseFloat(e.target.value) || 0))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="sc-mens-athletes" className="text-xs">Men's athletes</Label>
              <Input id="sc-mens-athletes" type="number" min="0" value={scMensAthletes}
                onChange={(e) => setScMensAthletes(Math.max(0, parseInt(e.target.value, 10) || 0))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="sc-womens-athletes" className="text-xs">Women's athletes</Label>
              <Input id="sc-womens-athletes" type="number" min="0" value={scWomensAthletes}
                onChange={(e) => setScWomensAthletes(Math.max(0, parseInt(e.target.value, 10) || 0))} />
            </div>
          </div>

          <div className="rounded-lg border bg-muted/40 p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Spend ratio (M : W)</div>
                <div className="font-semibold mt-0.5">
                  {scenarioCalc.spendRatio == null ? "—" : `${scenarioCalc.spendRatio.toFixed(2)} : 1`}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Participation ratio (M : W)</div>
                <div className="font-semibold mt-0.5">
                  {scenarioCalc.partRatio == null ? "—" : `${scenarioCalc.partRatio.toFixed(2)} : 1`}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Spend − Participation gap</div>
                <div
                  className={`font-semibold mt-0.5 ${toneClass(scenarioStatus.tone)}`}
                  aria-label={scenarioCalc.gapPP == null ? undefined : `${scenarioCalc.gapPP.toFixed(1)} percentage points`}
                >
                  {scenarioCalc.gapPP == null ? "—" : (
                    <>{scenarioCalc.gapPP.toFixed(1)} <AbbrPP /></>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Status</div>
                <span className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded ${toneBadge(scenarioStatus.tone)}`}>
                  {scenarioStatus.label}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={resetScenario}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Reset to current
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Equity Snapshot — current vs scenario */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Equity Snapshot — Current vs Scenario</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                <TableHead className="text-right">Current (FY {fy})</TableHead>
                <TableHead className="text-right">Scenario</TableHead>
                <TableHead className="text-right">Δ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                // Tone the entire Δ column on a single rule: how does the
                // scenario change move the women's spend−participation gap?
                // Closer to 0 → OK (improvement), no change → Watch (neutral),
                // further from 0 → Action (regression). Reuses the same
                // OK/Watch/Action palette as the rest of the equity panel.
                const gapDelta =
                  liveCalc.gapPP != null && scenarioCalc.gapPP != null
                    ? scenarioCalc.gapPP - liveCalc.gapPP
                    : null;
                const deltaTone: "ok" | "watch" | "action" =
                  gapDelta == null
                    ? "watch"
                    : Math.abs(scenarioCalc.gapPP ?? 0) < Math.abs(liveCalc.gapPP ?? 0)
                    ? "ok"
                    : Math.abs(scenarioCalc.gapPP ?? 0) > Math.abs(liveCalc.gapPP ?? 0)
                    ? "action"
                    : "watch";
                const deltaCellClass = `text-right font-medium ${toneClass(deltaTone)}`;
                const fmtDelta = (n: number) => (n > 0 ? `+${fmtMoney(n)}` : fmtMoney(n));
                const fmtDeltaInt = (n: number) => (n > 0 ? `+${n}` : String(n));
                const fmtDeltaRatio = (n: number) => (n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2));
                const fmtDeltaPp = (n: number) => (n > 0 ? `+${n.toFixed(1)}` : n.toFixed(1));
                const dRatioSpend =
                  liveCalc.spendRatio != null && scenarioCalc.spendRatio != null
                    ? scenarioCalc.spendRatio - liveCalc.spendRatio
                    : null;
                const dRatioPart =
                  liveCalc.partRatio != null && scenarioCalc.partRatio != null
                    ? scenarioCalc.partRatio - liveCalc.partRatio
                    : null;
                return (
                  <>
                    <TableRow>
                      <TableCell>Men's spend</TableCell>
                      <TableCell className="text-right">{fmtMoney(liveMensSpend)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(scMensSpend)}</TableCell>
                      <TableCell className={deltaCellClass}>{fmtDelta(scMensSpend - liveMensSpend)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Women's spend</TableCell>
                      <TableCell className="text-right">{fmtMoney(liveWomensSpend)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(scWomensSpend)}</TableCell>
                      <TableCell className={deltaCellClass}>{fmtDelta(scWomensSpend - liveWomensSpend)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Men's athletes</TableCell>
                      <TableCell className="text-right">{liveMensAthletes}</TableCell>
                      <TableCell className="text-right">{scMensAthletes}</TableCell>
                      <TableCell className={deltaCellClass}>{fmtDeltaInt(scMensAthletes - liveMensAthletes)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Women's athletes</TableCell>
                      <TableCell className="text-right">{liveWomensAthletes}</TableCell>
                      <TableCell className="text-right">{scWomensAthletes}</TableCell>
                      <TableCell className={deltaCellClass}>{fmtDeltaInt(scWomensAthletes - liveWomensAthletes)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Spend ratio (M : W)</TableCell>
                      <TableCell className="text-right">{liveCalc.spendRatio == null ? "—" : `${liveCalc.spendRatio.toFixed(2)} : 1`}</TableCell>
                      <TableCell className="text-right">{scenarioCalc.spendRatio == null ? "—" : `${scenarioCalc.spendRatio.toFixed(2)} : 1`}</TableCell>
                      <TableCell className={deltaCellClass}>{dRatioSpend == null ? "—" : fmtDeltaRatio(dRatioSpend)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Participation ratio (M : W)</TableCell>
                      <TableCell className="text-right">{liveCalc.partRatio == null ? "—" : `${liveCalc.partRatio.toFixed(2)} : 1`}</TableCell>
                      <TableCell className="text-right">{scenarioCalc.partRatio == null ? "—" : `${scenarioCalc.partRatio.toFixed(2)} : 1`}</TableCell>
                      <TableCell className={deltaCellClass}>{dRatioPart == null ? "—" : fmtDeltaRatio(dRatioPart)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        Gap (<AbbrPP />)
                      </TableCell>
                      <TableCell className={`text-right ${toneClass(liveStatus.tone)}`}>
                        {liveCalc.gapPP == null ? "—" : liveCalc.gapPP.toFixed(1)}
                      </TableCell>
                      <TableCell className={`text-right ${toneClass(scenarioStatus.tone)}`}>
                        {scenarioCalc.gapPP == null ? "—" : scenarioCalc.gapPP.toFixed(1)}
                      </TableCell>
                      <TableCell className={deltaCellClass}>{gapDelta == null ? "—" : fmtDeltaPp(gapDelta)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell className="text-right">
                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${toneBadge(liveStatus.tone)}`}>
                          {liveStatus.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${toneBadge(scenarioStatus.tone)}`}>
                          {scenarioStatus.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${toneBadge(deltaTone)}`}>
                          {deltaTone === "ok" ? "Improved" : deltaTone === "action" ? "Regressed" : "No change"}
                        </span>
                      </TableCell>
                    </TableRow>
                  </>
                );
              })()}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Participation counts roster */}
      <Card>
        <CardHeader><CardTitle className="text-base">Participation Counts ({fy})</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {isAdmin ? (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
              <div>
                <Label className="text-xs">Sport</Label>
                <Input value={newSport} onChange={(e) => setNewSport(e.target.value)} placeholder="Basketball" />
              </div>
              <div>
                <Label className="text-xs">Program</Label>
                <Select value={newGender} onValueChange={(v) => setNewGender(v as "mens" | "womens" | "coed")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mens">Men's</SelectItem>
                    <SelectItem value="womens">Women's</SelectItem>
                    <SelectItem value="coed">Co-ed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Athletes</Label>
                <Input type="number" min="0" value={newCount} onChange={(e) => setNewCount(e.target.value)} />
              </div>
              <Button onClick={handleAddRow} disabled={upsertMut.isPending}>
                <Plus className="h-4 w-4 mr-1" /> Save
              </Button>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">Participation counts are managed by Admins.</div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sport</TableHead>
                <TableHead>Program</TableHead>
                <TableHead className="text-right">Athletes</TableHead>
                {isAdmin && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(participation ?? []).map((row) => {
                const isEditing = editingId === row.id;
                return (
                  <TableRow key={row.id}>
                    <TableCell>{row.sport}</TableCell>
                    <TableCell className="capitalize">{row.programGender}</TableCell>
                    <TableCell className="text-right">
                      {isEditing ? (
                        <Input
                          type="number"
                          min="0"
                          value={editCount}
                          onChange={(e) => setEditCount(e.target.value)}
                          className="w-24 inline-block text-right"
                        />
                      ) : (
                        row.athleteCount
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        {isEditing ? (
                          <>
                            <Button
                              variant="default"
                              size="sm"
                              className="mr-1"
                              onClick={() => {
                                const n = parseInt(editCount, 10);
                                if (Number.isNaN(n) || n < 0) {
                                  toast({ title: "Invalid count", variant: "destructive" });
                                  return;
                                }
                                upsertMut.mutate(
                                  {
                                    data: {
                                      sport: row.sport,
                                      programGender: row.programGender,
                                      fiscalYear: row.fiscalYear,
                                      athleteCount: n,
                                    },
                                  },
                                  { onSuccess: () => setEditingId(null) },
                                );
                              }}
                              disabled={upsertMut.isPending}
                            >
                              Save
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingId(row.id);
                                setEditCount(String(row.athleteCount));
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => delMut.mutate({ id: row.id })}
                              disabled={delMut.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {(participation ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 4 : 3} className="text-center text-sm text-muted-foreground py-6">
                    No participation counts entered for {fy}.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {missingParticipation.length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs">
              <div className="font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Missing participation data for FY {fy}
              </div>
              <div className="mt-1 text-amber-900 dark:text-amber-100">
                Coaches exist for these sports but no athlete counts have been entered:
                <span className="ml-1 font-medium capitalize">{missingParticipation.join(", ")}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk gender assignment for coaches */}
      {isAdmin && unassignedCoaches.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Assign Program Gender ({unassignedCoaches.length} coaches unassigned)</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Coach</TableHead>
                  <TableHead>Sport</TableHead>
                  <TableHead className="w-48">Program Gender</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unassignedCoaches.slice(0, 25).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.firstName} {c.lastName}</TableCell>
                    <TableCell>{c.sport}</TableCell>
                    <TableCell>
                      <Select
                        onValueChange={(v) =>
                          updateCoachMut.mutate({
                            id: c.id,
                            data: { programGender: v as "mens" | "womens" | "coed" },
                          })
                        }
                      >
                        <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mens">Men's</SelectItem>
                          <SelectItem value="womens">Women's</SelectItem>
                          <SelectItem value="coed">Co-ed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {unassignedCoaches.length > 25 && (
              <div className="text-xs text-muted-foreground mt-2">
                Showing first 25 of {unassignedCoaches.length}. Save assignments above and refresh to see more.
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
